import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductWatchPreview from './components/ProductWatchPreview';
import ProductCustomizationPanel from './components/ProductCustomizationPanel';
import { customizableWatches } from '../../mocks/customizable-watches';
import { useCart } from '../../contexts/CartContext';

export default function ProductCustomizerPage() {
  const { watchType } = useParams<{ watchType: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const watchProduct = customizableWatches.find((w) => w.id === watchType);

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [activeTab, setActiveTab] = useState('');
  const [showAddedToCart, setShowAddedToCart] = useState(false);

  // Initialise default options & price when the product is loaded
  useEffect(() => {
    if (!watchProduct) {
      navigate('/collections');
      return;
    }

    const initialOptions: Record<string, string> = {};
    const options = watchProduct.customizationOptions;

    Object.entries(options).forEach(([key, values]) => {
      if (values && values.length > 0) {
        initialOptions[key] = values[0];
      }
    });

    setSelectedOptions(initialOptions);
    setTotalPrice(watchProduct.basePrice);

    const firstTab = Object.keys(options)[0];
    setActiveTab(firstTab || '');
  }, [watchProduct, navigate]);

  if (!watchProduct) {
    return null;
  }

  const handleOptionChange = (category: string, value: string) => {
    const newOptions = { ...selectedOptions, [category]: value };
    setSelectedOptions(newOptions);
    calculatePrice(newOptions);
  };

  const calculatePrice = (options: Record<string, string>) => {
    let price = watchProduct.basePrice;

    // CASE MATERIAL
    if (options.caseMaterial === 'Titanium') price += 300;
    if (options.caseMaterial === 'Two-Tone') price += 500;
    if (['Yellow Gold', 'Rose Gold', 'White Gold', 'Everose Gold'].includes(options.caseMaterial))
      price += 1500;
    if (options.caseMaterial === 'Platinum') price += 2500;
    if (options.caseMaterial === 'Bronze') price += 200;
    if (['Black DLC', 'ADLC Black'].includes(options.caseMaterial)) price += 400;
    if (options.caseMaterial === 'Carbon Fiber') price += 600;
    if (options.caseMaterial === 'Ceramic') price += 800;

    // BEZEL
    if (options.bezelType?.includes('Ceramic')) price += 200;
    if (options.bezelType === 'Diamond') price += 3000;
    if (options.bezelType === 'Baguette Diamond') price += 5000;
    if (options.bezelType === 'Sapphire Insert') price += 400;
    if (options.bezelType === 'Fluted') price += 100;
    if (options.bezelType === 'Engine-Turned') price += 150;

    // MOVEMENT
    if (options.movement?.includes('GMT')) price += 400;
    if (options.movement?.includes('High-Beat') || options.movement?.includes('High-Frequency')) price += 500;
    if (options.movement?.includes('Chronograph')) price += 600;
    if (options.movement?.includes('Ultra-Thin')) price += 700;
    if (options.movement?.includes('Perpetual')) price += 800;
    if (options.movement?.includes('COSC') || options.movement?.includes('Master Chronometer')) price += 300;
    if (options.movement?.includes('Skeleton')) price += 900;
    if (options.movement === 'Automatic 500m') price += 200;
    if (options.movement === 'High-Beat 1000m') price += 500;
    if (options.movement === 'Manual Wind') price += 200;
    if (options.movement === 'Automatic Power Reserve') price += 300;

    // BRACELET / STRAP
    if (options.braceletStrap === 'Jubilee Bracelet' || options.braceletStrap === 'President Bracelet')
      price += 200;
    if (options.braceletStrap === 'Alligator Strap') price += 300;
    if (options.braceletStrap === 'Racing Leather') price += 100;
    if (options.braceletStrap === 'Mesh Bracelet') price += 100;

    // INTEGRATION
    if (options.braceletIntegration === 'Two-Tone Integrated') price += 400;
    if (options.braceletIntegration === 'Gold Integrated') price += 1200;

    // OTHER OPTIONS
    if (options.subdialLayout === 'Tri-Compax') price += 100;
    if (options.pushers === 'Screw-Down Pushers') price += 150;
    if (options.dialColor === 'Skeleton') price += 800;
    if (options.hands === 'Diamond') price += 500;

    // ENGRAVING
    if (options.engraving && options.engraving.length > 0) price += 50;

    setTotalPrice(price);
  };

  const handleAddToCart = () => {
    const customProduct = {
      id: `${watchProduct.id}-${Date.now()}`,
      name: watchProduct.name,
      price: totalPrice,
      image: watchProduct.image,
      category: 'Custom Watch',
    };

    try {
      addToCart(customProduct, 1);
      setShowAddedToCart(true);
      setTimeout(() => setShowAddedToCart(false), 3000);
    } catch (error) {
      console.error('Failed to add custom watch to cart:', error);
      // Optional: display a user‑friendly error message here
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {watchProduct.name}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{watchProduct.description}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <ProductWatchPreview
              watchProduct={watchProduct}
              selectedOptions={selectedOptions}
              activeTab={activeTab}
            />
            <ProductCustomizationPanel
              watchProduct={watchProduct}
              selectedOptions={selectedOptions}
              onOptionChange={handleOptionChange}
              totalPrice={totalPrice}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onAddToCart={handleAddToCart}
            />
          </div>
        </div>
      </main>

      {showAddedToCart && (
        <div className="fixed bottom-8 right-8 bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-semibold">Added to Cart!</div>
            <div className="text-sm opacity-90">
              Your custom {watchProduct.shortName} has been added
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
